/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useEffect, useRef } from "react";
import { Col, Dropdown } from "react-bootstrap";
import ColorSwatch from "./ColorSwatch";
import { PortalShadeOption, PortalShadeOptionItem } from "../../Store/entities/assemblies/types";

type ColorOptionsDropdownProps = {
	title: string;
	defaultColor?: string | undefined;
	options: PortalShadeOptionItem<void>[] | PortalShadeOptionItem<string>[] | { value: string }[];
	onSelect: (value: string) => void;
	colorPalettes: PortalShadeOption<string>;
	selectedOption?: PortalShadeOptionItem<string> | undefined;
	className?: string;
	disabled?: boolean;
};

const ColorOptionsDropdown = ({
	title,
	defaultColor,
	options,
	onSelect,
	colorPalettes,
	selectedOption,
	className = "",
	disabled = false,
}: ColorOptionsDropdownProps) => {
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const handleToggle = (isOpen: boolean) => {
		setDropdownOpen(isOpen);
	};

	const handleSelect = (value: string) => {
		onSelect(value);
		setDropdownOpen(false);
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setDropdownOpen(false);
			}
		};

		if (dropdownOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [dropdownOpen]);
    
	const getShortLabel = (originalLabel: string): string => {
		const lowerLabel = originalLabel.toLowerCase();
		if (lowerLabel.includes("end cap") || lowerLabel.includes("endcap")) {
			return "End Caps Color";
		}
		if (lowerLabel.includes("hardware")) {
			return "Hardware Color";
		}
		if (lowerLabel.includes("bracket")) {
			return "Bracket Color";
		}
		if (lowerLabel.includes("mount")) {
			return "Mount Color";
		}
		return `${originalLabel.replace(/Color$/i, "").trim()} Color`;
	};

	const shortLabel = getShortLabel(title);

	return (
		<Col key={title} className="text-center">
			<label className="text-center">{shortLabel}</label>

			<div ref={dropdownRef}>
				<Dropdown show={dropdownOpen} onToggle={handleToggle} autoClose>
					<Dropdown.Toggle
						variant="light"
						className={`w-100 d-flex align-items-center justify-content-between bg-body mt-1 ${className}`}
						style={{
							padding: "0.375rem 0.75rem",
						}}
						disabled={disabled}
					>
						<span className="d-flex align-items-center">
							{selectedOption?.label || title}
							<ColorSwatch
								colorPalettes={colorPalettes}
								value={selectedOption?.value ?? ""}
								hexCode={selectedOption?.hexCode ?? ""}
								isOpen={dropdownOpen}
								size="small"
								defaultColor={defaultColor}
								hidden={selectedOption?.value === "Custom Palette"}
							/>
						</span>
					</Dropdown.Toggle>

					<Dropdown.Menu className="w-100" style={{ maxHeight: "300px", overflowY: "auto" }}>
						<div
							className=""
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
								gap: "8px",
							}}
						>
							{options.map((option) => (
								<div
									key={option.value}
									className="color-option d-flex flex-row align-items-center text-start rounded"
									style={{
										padding: "8px",
										border: option.value === selectedOption?.value
											? "2px solid #8dc63f"
											: "2px solid transparent",
										backgroundColor: "transparent",
										cursor: "pointer"
									}}
									onClick={() => handleSelect(option.value)}
								>
									<ColorSwatch
										colorPalettes={colorPalettes}
										value={option.value}
										selectedValue={selectedOption?.value ?? ""}
										hexCode={option.hexCode}
										size="small"
									/>
									<span
										className={`ms-2 ${
											option.value === selectedOption?.value ? "fw-bold" : ""
										}`}
									>
										{option.label}
									</span>
								</div>
							))}
						</div>
					</Dropdown.Menu>
				</Dropdown>
			</div>
		</Col>
	);
};

export default ColorOptionsDropdown;
